<template>
    <!--
        https://animod.atlassian.net/browse/ANI-1223
    -->
    <Form :errors="formErrors" id="checkout" @submitValid="placeOrder">
        <LayoutWithAside sticky-aside>
            <template v-slot:main>
                <Stepper>
                    <DetailsSection
                        v-if="!isMultivoucher && !isAccessCodeProduct && !isDelayPayment"
                        no-border
                        class="pb-0"
                    >
						<h1 class="sr-only">{{ `${$t('Checkout.header')} - ${checkout.productTitleForText}` }}</h1>

                        <CheckoutIntro
                            v-if="!checkout.auctionOffer"
                            :productTitle="checkout.productTitleForText"
                            :hide-voucher-protection="!isVoucherProtectionEnabled"
                        />
                        <TopSection class="checkout-intro" v-else>
                            <template v-slot:header>
                                <translate path="Checkout.intro.headerAuctionOffer">
                                    <template v-slot:title>
                                        <strong>{{ checkout.productTitleForText }}</strong>
                                    </template>
                                </translate>
                            </template>
                        </TopSection>

                        <CheckoutBidding
                            v-if="checkout.auctionOffer && $store.state.ident === 'nettovorteilswelt'"
                            :priceData="priceData"
                            :bid-amount.sync="bidAmount"
                            with-buttons
                            hide-low-warning
                        ></CheckoutBidding>
                    </DetailsSection>

                    <DetailsSection v-if="showShippingSelection" class="step">
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.step.delivery') }}
                        </template>

                        <ShippingMethodsSelect
                            :shippingMethods="checkout.shippingMethods"
                            :selected="shippingMethod"
                            @update:selected="onShippingChange"
                            :quantity="quantity"
                        />
                    </DetailsSection>

                    <DetailsSection
                        v-if="!isAccessCodeProduct && $getSafe(checkout, 'availableVoucherDesigns.length')"
                        class="step"
                    >
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.step.voucherImage') }}
                        </template>

                        <VoucherImageSelect
                            :availableVoucherDesigns="checkout.availableVoucherDesigns"
                            :selected.sync="selectedVoucherImage"
                        />
                    </DetailsSection>

                    <DetailsSection class="step">
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.step.personalData') }}
                        </template>

                        <AddressInputs :address-data="billingAddress" :extended="postShippingSelected" class="mb-0">
                            <h4
                                v-if="checkout.separateShippingAddressAllowed && postShippingSelected"
                                class="section-subheader"
                            >
                                {{ $t('Checkout.address.billingAddress') }}
                            </h4>

                            <div class="form-row" slot="middle">
                                <div class="form-group col-12 col-sm-6 col-lg">
                                    <input
                                        type="email"
                                        name="email"
                                        v-model.lazy="email"
                                        class="form-control input-gray"
                                        :placeholder="$t('Form.email.placeholder')"
                                        :required="emailAddressRequired"
                                        :aria-label="$t('Generic.email')"
                                    />
                                </div>

                                <div v-if="checkout.phoneNumberRequired" class="form-group col-12 col-sm-6">
                                    <input
                                        type="tel"
                                        name="phone"
                                        v-model.lazy="phone"
                                        class="form-control input-gray"
                                        :placeholder="$t('Form.phone.placeholder')"
                                        :aria-label="$t('Generic.phone')"
                                        required="required"
                                        maxlength="20"
                                    />
                                </div>

								<!-- Aldi Reisen specific fields -->
								<div v-if="checkout.zipcodeRequired && !postShippingSelected" class="form-group col-12 col-sm-6">
									<input
										type="text"
										name="zipcode"
										v-model.lazy="billingAddress.zipcode"
										inputmode="numeric"
										maxlength="5"
										minlength="5"
										pattern="\d*"
										class="form-control input-gray"
										:placeholder="$t('Generic.zipcode')"
										required
										:aria-label="$t('Generic.zipcode')"
									/>
								</div>

								<div v-if="checkout.cityRequired && !postShippingSelected" class="form-group col-12 col-sm-6">
									<input
										type="text"
										name="city"
										v-model.lazy="billingAddress.city"
										class="form-control input-gray"
										:placeholder="$t('Generic.city')"
										required
										:aria-label="$t('Generic.city')"
									/>
								</div>

								<!-- End of Aldi Reisen specific fields -->

                                <div v-if="isDeutschlandCardEnabled" class="form-group col">
                                    <input
                                        inputmode="numeric"
                                        name="loyaltyCardNumber"
                                        v-model.lazy="loyaltyCardNumber"
                                        class="form-control input-gray"
                                        :placeholder="$t('Form.loyaltyCardNumber.deutschlandcard.placeholder')"
                                        aria-label="DeutschlandCard Nummer"
                                        maxlength="10"
                                        pattern="\d+"
                                        @keypress="validateDigits"
                                    />
                                </div>
                                <div v-if="isPaybackEnabled" class="form-group col">
                                    <input
                                        inputmode="numeric"
                                        name="loyaltyCardNumber"
                                        v-model.lazy="loyaltyCardNumber"
                                        class="form-control input-gray"
                                        :placeholder="$t('Form.loyaltyCardNumber.payback.placeholder')"
                                        aria-label="Payback Kartennummer"
                                        maxlength="10"
                                        pattern="\d+"
                                        @keypress="validateDigits"
                                    />
                                </div>

                                <div v-if="checkout.membershipCardEnabled" class="form-group col">
                                    <input
                                        inputmode="numeric"
                                        name="membershipCardNumber"
                                        v-model.lazy="membershipCardNumber"
                                        class="form-control input-gray"
                                        :placeholder="$t('Form.membershipCardNumber.placeholder')"
                                        aria-label="Mitgliedsnummer"
                                        maxlength="10"
                                        pattern="\d+"
                                        required="required"
                                        @keypress="validateDigits"
                                    />
                                </div>
                            </div>
                        </AddressInputs>

                        <template v-if="checkout.separateShippingAddressAllowed && postShippingSelected">
                            <label class="form-check-label">
                                <input type="checkbox" v-model="differentShippingAddress" />
                                {{ $t('Checkout.address.differentDeliveryAddress') }}
                            </label>

                            <div>
                                <IconSvg filepath="info.svg" class="text-muted" />
                                {{ $t('Checkout.address.readyForShipment') }}
                            </div>
                        </template>

                        <AddressInputs
                            v-if="differentShippingAddress"
                            :address-data="shippingAddress"
                            :extended="postShippingSelected"
                        >
                            <h4 class="section-subheader">{{ $t('Checkout.address.shippingAddress') }}</h4>
                        </AddressInputs>
                    </DetailsSection>

                    <DetailsSection
                        v-if="
                            checkout.additionalInformationVisible &&
                            !(isArrivalDateSelectionPossible && !isDelayPayment)
                        "
                        class="step"
                    >
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Generic.extraWishes') }}
                        </template>

                        <textarea
                            v-model.lazy="additionalInformation"
                            :maxlength="checkout.maximumSizeForAdditionalInformation"
                            :placeholder="$t('Checkout.extraWishes.placeholder', { break: '\n' })"
                            rows="7"
                            class="form-control input-gray message"
                            :aria-label="$t('Checkout.extraWishes.placeholder')"
                        />
                    </DetailsSection>

                    <DetailsSection
                        v-if="
                            actualTravellerDataMode === 'FullDetails' ||
                            actualTravellerDataMode === 'InternationalDetails'
                        "
                        class="step"
                    >
                        <template v-slot:header>
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.travellerData.names') }}
                        </template>

                        <div
                            v-for="(person, index) in persons"
                            :key="'person' + index"
                            class="mb-lg-0"
                            :class="{ 'pt-3 mt-3 border-top': index }"
                        >
                            <!-- Placed here because helps deal with borders and replaces the entity it toggles (instead of being at the bottom of person#1) -->
                            <div
                                v-if="
                                    index === 1 && persons.length === 2
                                    /* not sure if we'd like to allow this for more travellers */
                                "
                                class="form-row"
                                :class="{ 'mb-2': !isTravellingAlone }"
                            >
                                <label class="form-check-label">
                                    <input type="checkbox" v-model="isTravellingAlone" />

                                    {{ $t('Checkout.travellerData.travelByMyself') }}
                                </label>
                            </div>

                            <!--
                                Hiding the second person data instead of making readonly and performing any data copy at this stage, because
                                - preserves state when checkbox is toggled
                                - simplifies validation
                                - reduces mobile checkout height when checked
                                - copying person#2 on toggle will need to trigger validation and assume person#1 is already filled correctly
                            -->
                            <template v-if="!isTravellingAlone || index !== 1">
                                <header class="mb-1">{{ $t('Generic.person', null, 1) }} #{{ index + 1 }}</header>

                                <div class="form-row">
                                    <div class="form-group col-12 col-sm">
                                        <input
                                            type="text"
                                            v-model="person.firstname"
                                            class="form-control input-gray"
                                            required
                                            :placeholder="$t('Generic.firstname')"
                                            maxlength="30"
                                            :aria-label="$t('Generic.firstname')"
                                        />
                                    </div>
                                    <div class="form-group col-12 col-sm">
                                        <input
                                            type="text"
                                            v-model="person.lastname"
                                            class="form-control input-gray"
                                            required
                                            :placeholder="$t('Generic.lastname')"
                                            maxlength="30"
                                            :aria-label="$t('Generic.lastname')"
                                        />
                                    </div>

                                    <div
                                        v-if="checkout.travellerDataMode === 'InternationalDetails'"
                                        class="form-group col-12 col-sm"
                                    >
                                        <input
                                            type="text"
                                            v-model="person.nationality"
                                            class="form-control input-gray"
                                            :placeholder="$t('Generic.nationality')"
                                            required
                                            :aria-label="$t('Generic.nationality')"
                                        />
                                    </div>
                                </div>
                                <div class="form-row">
                                    <div class="form-group col-auto">
                                        <DateInput
                                            v-model="person.birthdate"
                                            :label="$t('Generic.birthdate')"
                                            required
                                        />
                                    </div>
                                </div>
                            </template>
                        </div>
                    </DetailsSection>

                    <DetailsSection v-if="actualTravellerDataMode === 'PersonCount'" class="step">
                        <template v-slot:header>
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.travellerData.details') }}
                        </template>

                        <div class="form-row align-items-center mb-0">
                            <label for="amountOfAdults" class="col-6 col-sm-3 col-form-label">
                                {{ $t('Checkout.travellerData.adultsTotal') }}
                            </label>
                            <div class="col-6 col-sm-2 mb-3 mb-sm-0">
                                <input
                                    type="number"
                                    v-model="amountOfAdults"
                                    class="form-control input-gray"
                                    id="amountOfAdults"
                                    :min="1"
                                    :max="numberOfPersons"
                                />
                            </div>

                            <label
                                for="amountOfChildren"
                                class="col-6 col-sm-3 offset-sm-2 col-form-label text-sm-right"
                            >
                                {{ $t('Checkout.travellerData.infantsTotal') }}
                            </label>
                            <div class="col-6 col-sm-2">
                                <input
                                    type="number"
                                    v-model="amountOfInfants"
                                    class="form-control input-gray"
                                    id="amountOfChildren"
                                    min="0"
                                    max="9"
                                />
                            </div>
                        </div>

                        <div v-if="amountOfInfants > 0" class="mb-0 mt-3">
                            {{ $t('Checkout.travellerData.infantsAge') }}
                        </div>
                        <div class="row">
                            <div
                                v-for="(infant, index) in infants"
                                class="col-6 col-sm-3 input-group align-items-center mt-3"
                                :key="'infant' + index"
                            >
                                <label :for="'infant' + index" class="mr-3 mb-0"
                                    >{{ $t('Generic.infant') }} {{ index + 1 }}</label
                                >
                                <select
                                    v-model="infant.age"
                                    class="form-control input-gray custom-select"
                                    :id="'infant' + index"
                                    required
                                >
                                    <option v-for="(age, index) in 18" :value="index" :key="'ageOption' + index">
                                        {{ index }} {{ $t('Generic.year', null, index) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                    </DetailsSection>

                    <DetailsSection v-if="isArrivalDateSelectionPossible && !isDelayPayment" class="step">
                        <template v-slot:header>
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.section.multivoucher') }}
                        </template>

                        <ArrivalDateMode
                            :product-variant-id="finalProductVariantId"
                            :nights="checkout.variantCheckoutData.numberOfNights"
                            :arrival-date-mode.sync="arrivalDateSelectionMode"
                            :checkin-dates.sync="delayPayment.checkinDates"
                            :additional-information.sync="delayPayment.additionalInformation"
                            :additional-information-visible="checkout.additionalInformationVisible"
                        />
                        <span
                            v-if="
                                arrivalDateSelectionMode === 'date' &&
                                checkout.variantCheckoutData.cancellationSettings.cancellationMode === 'NON_CANCELABLE'
                            "
                        >
                            <p>
                                {{ $t('Checkout.cancellationOptin.text') }}
                            </p>
                            <label class="form-check-label">
                                <input
                                    type="checkbox"
                                    v-model="cancellationOptin"
                                    aria-label="Storno-Zustimmung"
                                    placeholder="Storno-Zustimmung"
                                    required
                                />
                                {{ $t('Checkout.cancellationOptin.label') }}
                            </label>
                        </span>
                    </DetailsSection>

                    <DetailsSection v-if="isMultivoucher" class="step">
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.step.voucherCode') }}
                        </template>

                        <Discount :valueVouchers.sync="valueVouchers" />

                        <media-only desktop>
                            <SecurityInfo v-if="!showPaymentMethods" />
                        </media-only>
                    </DetailsSection>

                    <DetailsSection v-if="checkout.accessCodeRequired" class="step">
                        <template slot="header">
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.step.accessCode') }}
                        </template>

                        <div class="form-group">
                            <input
                                type="text"
                                name="accessCode"
                                v-model.lazy="accessCode"
                                class="form-control input-gray"
                                :placeholder="$t('Form.accessCode.placeholder')"
                                required
                                :aria-label="$t('Generic.accessCode')"
                            />
                        </div>
                    </DetailsSection>

                    <DetailsSection v-if="showPaymentMethods" class="step" no-border>
                        <template v-slot:header>
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            {{ $t('Checkout.section.paymentData') }}
                        </template>

                        <PaymentMethodsSelect
                            v-if="priceData.amountToPay > 0"
                            :paymentMethods="paymentMethods"
                            :selected.sync="paymentMethod"
                            :postShippingSelected="postShippingSelected"
                            :ctaText="$t('Checkout.submit.buyNow')"
                            ref="paymentMethodComponent"
                        />
                        <p v-else>{{ $t('Checkout.payment.noPaymentNeeded') }}</p>

                        <media-only desktop>
                            <SecurityInfo />
                        </media-only>
                    </DetailsSection>

                    <DetailsSection v-else-if="isDelayPayment" class="step" no-border>
                        <template v-slot:header>
                            <media-only desktop no-wrap class="step-number" tag="span" />
                            <template v-if="checkout.backedByContingents">
                                {{ $t('Checkout.section.delayPayment.single') }}
                            </template>
                            <template v-else>
                                {{ $t('Checkout.section.delayPayment') }}
                            </template>
                        </template>
                        <ArrivalDateSelection
                            :productVariantId="finalProductVariantId"
                            :arrival-date="arrivalDate"
                            :checkinDates.sync="delayPayment.checkinDates"
                            :additional-information.sync="delayPayment.additionalInformation"
                        />
                        <p>{{ $t('Checkout.delayPayment.notice') }}</p>
                        <p>{{ $t('Checkout.delayPayment.notice2') }}</p>
                    </DetailsSection>
                </Stepper>
            </template>

            <template v-slot:aside>
                <OrderAside>
                    <media-only :desktop="!$route.query.mobileTeaser">
                        <Teaser v-skeleton="checkout.imageUrl" :background-url="checkout.imageUrl" class="aside-teaser">
                            <DetailsTeaser>
                                <div v-if="$getSafe(variantCheckoutDataComputed, 'hotelData.name')">
                                    <Classification
                                        :hotelClassification="variantCheckoutDataComputed.hotelData.classification"
                                    />
                                    <div class="hotel-name">
                                        {{ $getSafe(variantCheckoutDataComputed, 'hotelData.name') }}
                                    </div>
                                </div>
                            </DetailsTeaser>
                        </Teaser>
                    </media-only>

                    <DetailsSection v-if="arrivalDate">
                        <template v-slot:header>
                            {{ $t('Generic.travelPeriod') }}
                        </template>
                        <p class="inline-justified indented mb-1">
                            <span>{{ $t('Generic.checkIn') }}:</span>
                            <span>{{ arrivalDate }}</span>
                        </p>
                        <p class="inline-justified indented mb-1" v-if="variantCheckoutDataComputed.departureDate">
                            <span>{{ $t('Generic.checkOut') }}:</span>
                            <span>{{ variantCheckoutDataComputed.departureDate }}</span>
                        </p>
                        <p class="inline-justified indented mb-1">
                            <span>{{ $t('Generic.duration') }}:</span>
                            <span>{{ variantCheckoutDataComputed.duration }}</span>
                        </p>
                        <p class="inline-justified indented mb-1" v-if="isCancellationInfoAvailable">
                            <span>{{ $t('Generic.cancellationInfo') }}:</span>
                            <span v-if="isNonCancelableProduct" class="non-cancelable">{{ cancellationInfo }}</span>
                            <span v-else>{{ cancellationInfo }}</span>
                        </p>
                    </DetailsSection>

                    <DetailsSection v-if="checkout.auctionOffer && hasVariantData" class="darker">
                        <VariantData :variantData="variantCheckoutDataComputed" modal-allowed inline />
                    </DetailsSection>

                    <DetailsSection v-if="!isAccessCodeProduct && checkout.quantitySelectionVisible" class="darker">
                        <template v-slot:header>
                            {{ isMultivoucher ? $t('Generic.travel') : $t('Generic.number') }}
                        </template>

                        <div class="d-flex">
                            <QuantitySelect
                                v-if="!isMultivoucher"
                                :availableQuantity="maximumQuantity"
                                :selected.sync="quantity"
                                class="quantity"
                            />

                            <VariantData
                                v-if="hasVariantData"
                                :variantData="variantCheckoutDataComputed"
                                modal-allowed
                                :is-direct-booking="!!arrivalDate"
                                :inline="!isMultivoucher"
                                :class="{ indented: isMultivoucher }"
                            />
                        </div>

                        <p v-if="showQuantityNotice" class="post-quantity-notice" ref="quantityNotice">
                            {{ $t('Checkout.message.quantityGiftbox') }}
                        </p>
                    </DetailsSection>

                    <DetailsSection v-if="checkout.addonOptions && checkout.addonOptions.length" class="px-0">
                        <UpsellingCheckoutOption
                            v-for="(option, index) in checkout.addonOptions"
                            :option="option"
                            :initial-price="checkout.price"
                            :key="'upsellingOption' + index"
                            :active="option.variantId === (addonVariantSelected && addonVariantSelected.variantId)"
                            @click.native="setAddonVariant(option)"
                            class="m-0"
                        />
                    </DetailsSection>

                    <DetailsSection
                        v-if="
                            !isMultivoucher && (checkout.valueVoucherUsageAllowed || checkout.discountCodeUsageAllowed)
                        "
                        class="discount-aside-section"
                    >
                        <template v-slot:header>
                            <span class="discount-header">{{ $t('Checkout.discount.header') }}</span>
                            <button
                                type="button"
                                class="discount-toggle btn btn-link text-uppercase p-0"
                                @click.stop="toggleDiscount = !toggleDiscount"
                            >
                                {{ $t('Checkout.discount.redeemHere') }}
                                <IconSvg filepath="arrow-down.svg" :class="{ active: toggleDiscount }" />
                            </button>
                        </template>

                        <Discount
                            extended
                            :productVariantId="finalProductVariantId"
                            :totalProductPrice="priceData.totalProductPrice"
                            :email="email"
                            :discountCoupon.sync="discountCoupon"
                            :valueVouchers.sync="valueVouchers"
                            :showInput="toggleDiscount"
                            :remainingVoucherCredit="priceData.remainingVoucherCredit"
                            :style="{ marginTop: toggleDiscount ? 0 : '-10px' }"
                        />
                    </DetailsSection>

                    <DetailsSection no-border class="darker-mobile final">
                        <PriceData v-if="!checkout.auctionOffer && showPriceData" :priceData="priceData" />

                        <template v-if="checkout.auctionOffer">
                            <CheckoutBidding
                                v-if="$store.state.ident !== 'nettovorteilswelt'"
                                :priceData="priceData"
                                :bid-amount.sync="bidAmount"
                                :hideLowWarning="$store.state.ident === 'mivo'"
                            ></CheckoutBidding>
                            <NettoBiddingCheckoutSummary v-else :bid-amount="bidAmount" />
                        </template>

                        <DeutschlandCardPoints
                            v-if="isDeutschlandCardEnabled"
                            :amountToPay="priceData.amountToPay"
                            :extra-points="checkout.extraPoints"
                        />

                        <PaybackPoints
                            v-if="isPaybackEnabled"
                            :amountToPay="priceData.amountToPay"
                            :extra-points="checkout.extraPoints"
                        />

                        <div>
                            <button
                                v-if="!isProceeding"
                                :disabled="(checkout.auctionOffer && !bidAmount) || checkout.checkoutAvailableFrom"
                                class="btn btn-primary btn-lg btn-block purchase-button"
                                type="submit"
                            >
                                {{ buttonText }}
                            </button>

                            <div v-else class="text-center" style="max-height: 46px">
                                <IconSvg filepath="spinner.svg" class="proceed-indicator"></IconSvg>
                                <p class="mb-0">{{ $t('Generic.pleaseWait') }}...</p>
                            </div>
                        </div>
                    </DetailsSection>

                    <DetailsSection no-border class="legal-container">
                        <LegalInfo
                            :legalInfo="legalInfo"
                            :showNewsletterTerms="!isDelayPayment"
                            :newsletter-accepted.sync="newsletterAccepted"
                            :additional-consent-given.sync="additionalPlatformConsentGiven"
                        />

                        <media-only mobile>
                            <SecurityInfo />
                        </media-only>
                    </DetailsSection>
                </OrderAside>
            </template>
        </LayoutWithAside>
    </Form>
</template>

<script>
import CheckoutIntro from '@/components/checkout/CheckoutIntro';
import DetailsSection from '@/components/DetailsSection';
import ShippingMethodsSelect from '@/components/checkout/ShippingMethodsSelect';
import VoucherImageSelect from '@/components/checkout/VoucherImageSelect';
import AddressInputs from '@/components/checkout/AddressInputs';
import Discount from '@/components/checkout/Discount';
import PaymentMethodsSelect from '@/components/checkout/PaymentMethodsSelect';
import SecurityInfo from '@/components/checkout/SecurityInfo';
import Teaser from '@/components/teasers/Teaser';
import DetailsTeaser from '@/components/teasers/DetailsTeaser';
import Classification from '@/components/Classification';
import QuantitySelect from '@/components/checkout/QuantitySelect';
import VariantData from '@/components/checkout/VariantData';
import CheckoutBidding from '@/components/checkout/CheckoutBidding.vue';
import PriceData from '@/components/checkout/PriceData';
import LegalInfo from '@/components/checkout/LegalInfo';
import Form from '@/components/Form';
import skeleton from '@/directives/skeleton';
import getHead from '@/mixins/getHead';
import LayoutWithAside from '@/components/LayoutWithAside';
import Stepper from '@/components/checkout/Stepper';
import OrderAside from '@/components/checkout/OrderAside';
import checkoutCommon from '@/mixins/checkoutCommon';
import availableShippingMethods from '@/models/shippingMethods';
import ArrivalDateSelection from '@/components/checkout/ArrivalDateSelection';
import extraScriptCode from '@/mixins/extraScriptCode';
import fetchResource from '@/utils/fetchResource';
import allPaymentMethods from '@/models/paymentMethods';
import DeutschlandCardPoints from '@/components/checkout/DeutschlandCardPoints';
import PaybackPoints from '@/components/checkout/PaybackPoints';
import ArrivalDateMode from '@/components/checkout/ArrivalDateMode';
import UpsellingCheckoutOption from '@/components/checkout/UpsellingCheckoutOption';
import TopSection from '@/components/checkout/TopSection.vue';
import NettoBiddingCheckoutSummary from '@/themes/nettovorteilswelt/components/checkout/NettoBiddingCheckoutSummary.vue';
import DateInput from '@/components/checkout/DateInput.vue';
import ArrivalDateSelectionModes from '@/models/ArrivalDateSelectionModes';

export default {
    name: 'Checkout',
    layout: ({ layout }) => layout,
    directives: {
        skeleton
    },
    mixins: [getHead, checkoutCommon, extraScriptCode('checkout')],
    head() {
        return this.getHead({
            pageTitle: `${this.$t('Checkout.header')} - ${this.checkout.productTitleForText}`,
            noindex: true
        });
    },
    async asyncData({ query }) {
        return {
            quantity: query.quantity || 1,
            productVariantId: query.productVariantId,
            valueVoucherProductId: query.voucherProductId,
            arrivalDate: query.arrivalDate,
            selectedVoucherImage: query.designId,
            bidAmount: parseInt(query.bidAmount, 10) || null
        };
    },
    async fetch(context) {
        const formattedArrivalDate = context.query.arrivalDate && context.query.arrivalDate.replace(/-/g, '.');
        let contactId = context.$cookies.contactid;
        await fetchResource(
            context,
            context.store.dispatch('getCheckout', {
                ...context.query,
                ...(formattedArrivalDate ? { arrivalDate: formattedArrivalDate } : null),
                ...(contactId ? { contactId : contactId}: null)
            })
        );
    },
    components: {
        PaybackPoints,
        DateInput,
        NettoBiddingCheckoutSummary,
        TopSection,
        ArrivalDateMode,
        DeutschlandCardPoints,
        CheckoutIntro,
        DetailsSection,
        Teaser,
        DetailsTeaser,
        Classification,
        Form,
        ShippingMethodsSelect,
        VoucherImageSelect,
        PaymentMethodsSelect,
        Discount,
        QuantitySelect,
        AddressInputs,
        PriceData,
        CheckoutBidding,
        LegalInfo,
        VariantData,
        SecurityInfo,
        LayoutWithAside,
        Stepper,
        OrderAside,
        ArrivalDateSelection,
        UpsellingCheckoutOption
    },
    data() {
        return {
            //user data
            billingAddress: {},
            email: '',
            phone: '',
            differentShippingAddress: false,
            shippingAddress: {},
            shippingMethod: null,
            additionalInformation: '',
            loyaltyCardNumber: '',
            membershipCardNumber: '',
            additionalPlatformConsentGiven: false,

            arrivalDateSelectionMode: ArrivalDateSelectionModes.NO_DATE,

            cancellationOptin: false,

            voucherCode: '',
            activationCode: null,
            accessCode: null,
            //new discount properties
            discountCoupon: {},
            valueVouchers: [],
            toggleDiscount: false,

            // travellerData
            amountOfInfants: 0,
            amountOfAdults: 2,
            infants: [],
            isTravellingAlone: false,

            //quantity
            quantity: this.$route.query.quantity || 1,

            //delayed payment
            delayPayment: {
                checkinDates: [],
                additionalInformation: ''
            },
            // upselling
            addonVariantSelected: null,

            // auction based offers
            bidAmount: null
        };
    },
    computed: {
        buttonText() {
            if (this.checkout.checkoutAvailableFrom) {
                return this.$t('Checkout.checkoutAvailableFrom', { date: this.checkout.checkoutAvailableFrom });
            } else if (this.checkout.auctionOffer) {
                // needs to precede voucher redemption case?
                return this.$t('Checkout.submit.bidNow');
            } else if (!this.priceData.amountToPay) {
                return this.$t('Checkout.submit.redeemNow');
            } else {
                return this.$t('Checkout.submit.buyNow');
            }
        },
        checkout() {
            return this.$store.state.checkout;
        },
        variantCheckoutDataComputed() {
            //making a deep copy in order to prevent obj modification outside mutation
            const activeVariantCheckoutData =
                (this.addonVariantSelected && this.addonVariantSelected.variantCheckoutData) ||
                this.checkout.variantCheckoutData;
            return (
                JSON.parse(JSON.stringify(activeVariantCheckoutData) || null) || {
                    hotelData: {
                        classification: {}
                    }
                }
            );
        },
        finalProductVariantId() {
            return (this.addonVariantSelected && this.addonVariantSelected.variantId) ?? this.productVariantId;
        },
        legalInfo() {
            return this.checkout.legalInfo || {};
        },
        emailAddressRequired() {
            return !this.checkout.privilegedClient || this.shippingMethod.toLocaleLowerCase() === 'email';
        },
        showShippingSelection() {
            return (
                !this.isMultivoucher &&
                (Object.keys(this.checkout.shippingMethods) || []).length > 1 &&
                this.$getSafe(this.checkout, 'shippingMethods.Post') !== null
            );
        },
        postShippingSelected() {
            const shippingLowercase = this.shippingMethod.toLocaleLowerCase();
            return shippingLowercase === 'post' || shippingLowercase === 'gift' || shippingLowercase === 'giftbox';
        },
        paymentMethods() {
            //be sure to make a shallow copy
            const paymentMethods = [...(this.checkout.paymentMethods || [])].filter(method =>
                //prepayment for only orders more than 10 EUR
                this.priceData.amountToPay < 1000 ? method !== allPaymentMethods.prepayment.value : true
            );
            return paymentMethods;
        },
        priceData() {
            const priceObj = this.$getSafe(this.addonVariantSelected || {}, 'price') || this.checkout.price || {};
            //regularPriceValues - webshop prices
            //surcharge - possible multivoucher surcharge
            if (!priceObj.regularPriceValues && !priceObj.surcharge) {
                return {};
            }

            if (priceObj.surcharge && priceObj.surcharge !== null && priceObj.surcharge > 0) {
                return {
                    amountToPay: priceObj.surcharge,
                    orderTotal: priceObj.surcharge,
                    surcharge: priceObj.surcharge
                };
            }

            const shippingCosts =
                this.checkout.shippingMethods && this.shippingMethod
                    ? this.checkout.shippingMethods[this.shippingMethod]
                    : 0;

            const singleProductPrice = priceObj.regularPriceValues.price;
            const totalProductPrice = priceObj.regularPriceValues.price * this.quantity;
            const totalListPrice = priceObj.regularPriceValues.listPrice * this.quantity;
            const totalDiscount = this.discountValue;
            const orderTotal = totalProductPrice - totalDiscount + shippingCosts;
            const amountToPay = Math.max(0, orderTotal - this.valueVoucherRedeemed);
            const totalSaving = totalDiscount + Math.max(0, totalListPrice - totalProductPrice); //swapped totalListPrice and totalProductPrice
            const remainingVoucherCredit = Math.max(0, this.valueVoucherRedeemed - orderTotal);
            //absent variant data can be treated as a voucher product
            const voucherValue = !this.hasVariantData ? priceObj.regularPriceValues.price : null;

            return {
                shippingCosts,
                singleProductPrice,
                totalProductPrice,
                totalListPrice,
                totalDiscount,
                orderTotal,
                amountToPay,
                totalSaving,
                remainingVoucherCredit,
                voucherValue
            };
        },
        showPaymentMethods() {
            return !!this.$getSafe(this.checkout, 'paymentMethods.length') && !this.isDelayPayment;
        },
        maximumQuantity() {
            return this.checkout.maximumQuantity;
        },
        isAccessCodeProduct() {
            return this.$getSafe(this.checkout, 'variantCheckoutData.webshopAccessCodeProduct');
        },
        isVoucherProtectionEnabled() {
            return this.checkout.voucherProtectionEnabled;
        },
        isDeutschlandCardEnabled() {
            return this.$getSafe(this.checkout, 'loyaltyProgram') === 'DeutschlandCard';
        },
        isPaybackEnabled() {
            return this.$getSafe(this.checkout, 'loyaltyProgram') === 'Payback';
        },
        isCancellationInfoAvailable() {
            let cancellationMode = this.$getSafe(this.checkout, 'variantCheckoutData.cancellationSettings.cancellationMode');
            return cancellationMode === 'NON_CANCELABLE'
                    || cancellationMode === 'CANCELABLE_AFTER_CONSULTATION'
                    || cancellationMode === 'CANCELABLE'
                ;
        },
        cancellationInfo() {
            let cancellationMode = this.$getSafe(this.checkout, 'variantCheckoutData.cancellationSettings.cancellationMode');
            if (cancellationMode === 'NON_CANCELABLE') {
                return this.$t('Checkout.productInfo.cancellationInfo.nonCancelable');
            } else if (cancellationMode === 'CANCELABLE_AFTER_CONSULTATION') {
                return this.$t('Checkout.productInfo.cancellationInfo.cancelableAfterConsultation');
            } else if (cancellationMode === 'CANCELABLE') {
                let cancellationDate = this.$getSafe(this.checkout, 'variantCheckoutData.cancellationSettings.cancellationDate');
                let thresholdInDays = this.$getSafe(this.checkout, 'variantCheckoutData.cancellationSettings.thresholdForFreeCancellation');
                if (cancellationDate) {
                  return this.$t('Checkout.productInfo.cancellationInfo.cancelableUntil', { date: cancellationDate });
                }
                return this.$t('Checkout.productInfo.cancellationInfo.cancelable', { days: thresholdInDays});
            } else {
                return null;
            }
        },
        isNonCancelableProduct() {
            return this.$getSafe(this.checkout, 'variantCheckoutData.cancellationSettings.cancellationMode') === 'NON_CANCELABLE';
        },
        discountValue() {
            return Math.max(0, this.discountCoupon.value || 0);
        },
        valueVoucherRedeemed() {
            return this.valueVouchers.reduce((final, current) => {
                final += current.value;
                return final;
            }, 0);
        },
        hasVariantData() {
            return (
                this.checkout &&
                this.checkout.variantCheckoutData &&
                !this.$isEmptyObject(this.checkout.variantCheckoutData)
            );
        },
        showQuantityNotice() {
            return this.quantity > 1 && this.shippingMethod === availableShippingMethods.giftBox.value;
        },
        //Used for travellerDataModes https://gitlab.icslabs.de/animod/animod-libraries/issues/70
        numberOfPersons() {
            return this.variantCheckoutDataComputed.numberOfPersons || 2;
        },
        persons() {
            return Array.from(Array(+this.amountOfAdults)).map(() => ({ }));
        },
        showPriceData() {
            return Object.keys(this.priceData).length > 0;
        },
        actualTravellerDataMode() {
            if (
                this.isMultivoucher &&
                this.isArrivalDateSelectionPossible &&
                this.arrivalDateSelectionMode === ArrivalDateSelectionModes.NO_DATE
            ) {
                return 'NoData';
            }
            return this.checkout.travellerDataMode;
        }
    },
    watch: {
        amountOfInfants(amount) {
            this.infants = Array.from(Array(+amount)).map((item, index) => {
                return this.infants[index] || { age: 1 };
            });
        },
        paymentMethod: {
            handler(value) {
                if (this.$gtm && this.checkout) {
                    this.$gtm.pushPaymentMethodChanged(
                        this.priceData.amountToPay,
                        this.constructGtmItem(),
                        this.shippingMethod,
                        value
                    );
                }
            },
            deep: true
        },
        checkout: {
            handler() {
                if (this.checkout.travellerDataMode === 'FullDetails') {
                    this.amountOfAdults = this.numberOfPersons * +this.quantity;
                } else if (this.checkout.travellerDataMode === 'InternationalDetails') {
                    this.amountOfAdults = this.numberOfPersons * +this.quantity;
                } else if (this.checkout.travellerDataMode === 'PersonCount') {
                    this.amountOfAdults = this.numberOfPersons;
                } else {
                    this.amountOfAdults = 0;
                }
            },
            immediate: true
        }
    },
    methods: {
        onShippingChange(method) {
            // moved to a separate method: shipping watcher gets called on created hook's preselect
            // this issue is caused by necessity to use data.shippingMethod since it is set in created hook from multiple possible sources
            // computed prop with get / set won't help here
            this.shippingMethod = method;

            if (this.$gtm && this.checkout) {
                this.$gtm.pushShippingMethodChanged(
                    this.priceData.amountToPay,
                    this.constructGtmItem(),
                    method,
                    this.paymentMethod
                );
            }
        },
        sendGTMEvent() {
            if (this.$gtm && this.checkout) {
                this.$gtm.pushCheckoutData(this.checkout, this.priceData);
                this.$gtm.pushBeginCheckout(
                    this.priceData.amountToPay || 0,
                    this.constructGtmItem(),
                    this.shippingMethod,
                    this.paymentMethod
                );
            }
        },
        validateDigits(e) {
            if (!/^[0-9]*$/.test(e.key)) {
                e.preventDefault();
            }
        },
        setAddonVariant(variant) {
            this.addonVariantSelected =
                this.addonVariantSelected && this.addonVariantSelected.variantId === variant.variantId ? null : variant;
        },
        preselectShippingMethod() {
            // From query string, has higher priority
            if (this.$route.query.shipping) {
                const foundMatch = Object.values(availableShippingMethods).find(
                    s => s.value === this.$route.query.shipping
                );
                if (foundMatch) {
                    this.shippingMethod = this.$route.query.shipping;
                    return;
                }
            }
            // backend checkout response dependent
            if (this.checkout.shippingMethods) {
                const possibleShippingKeys = Object.keys(this.checkout.shippingMethods);

                // preselect if only 1 method available
                if (possibleShippingKeys.length === 1) {
                    this.shippingMethod = possibleShippingKeys[0];
                }
                // Preselect Email if available
                else if (possibleShippingKeys.includes('Email')) {
                    this.shippingMethod = availableShippingMethods.email.value;
                }
                // preselect first available?
            }
        }
    },
    created() {
        this.preselectShippingMethod();
    },
    beforeMount() {
        this.$store.commit('setMobileHeaderTitle', this.$t('Checkout.header'));
        this.sendGTMEvent();
    }
};
</script>
<style scoped lang="scss">
.post-quantity-notice {
    border: 2px solid hsla(19, 100%, 59%, 0.5);
    padding: 5px 15px;
    margin-bottom: 10px;
    background: hsl(0, 0%, 95%);
}
.checkout-available-from {
    font-weight: bold;
    color: #930202;
}
span.non-cancelable {
    color: #bf0202;
}
</style>
